import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MenuItem from '@material-ui/core/MenuItem';
import useGetTranslatedAttribute from 'hooks/useGetTranslatedAttribute';

import ApiEntitySelectDialog from '../../../../components/ApiEntitySelectDialog/ApiEntitySelectDialog';
import CustomInput, { Props as CustomInputProps } from '../../../../components/CustomInput/CustomInput';
import Material, { EmptyMaterial } from '../../../../types/Material';
import api from '../../../../utils/api';

interface Props {
    name?: string;
    baseInputProps: Omit<CustomInputProps, 'name'>;
    onlyPlastic?: boolean;
    onlyNotPlastic?: boolean;
    onlyAllowedMaterisl?: boolean;
}

const MaterialSelect = (props: Props) => {
    const { name, baseInputProps, onlyPlastic, onlyNotPlastic, onlyAllowedMaterisl } = props;
    const { data, handleAttributeChange } = baseInputProps;

    const { t } = useTranslation();
    const getTranslatedAttribute = useGetTranslatedAttribute();

    const [materialData, setMaterialData] = useState<Material | null>(null);

    const [isSelectModalOpen, setIsSelectModalOpen] = useState(false);

    const onMaterialSelect = useCallback((selectedMaterialId?: number) => {
        if (selectedMaterialId && handleAttributeChange) {
            handleAttributeChange(name ?? 'materialId', selectedMaterialId.toString());
        }
        setIsSelectModalOpen(false);
    }, [name, handleAttributeChange]);

    const materialId = data[name ?? 'materialId'];

    useEffect(() => {
        if (parseInt(materialId, 10) === -1) {
            setMaterialData({ ...EmptyMaterial, id: -1, code: 'Altro', description: 'Altro' });

            if (handleAttributeChange) {
                handleAttributeChange('__name_' + (name ?? 'materialId'), 'Altro');
            }
        } else if (materialId) {
            api.request('/materials/' + materialId).then((res) => {
                setMaterialData(res);

                if (handleAttributeChange) {
                    handleAttributeChange('__name_' + (name ?? 'materialId'), getTranslatedAttribute(res, 'description'));
                }
            });
        }
        else {
            setMaterialData(null);
        }
    }, [getTranslatedAttribute, handleAttributeChange, name, materialId]);

    const map = useCallback((material: Material) => {
        if (onlyPlastic && !material.isPlastic) return null;
        if (onlyNotPlastic && (material.isPlastic || !material.isCompostable)) return null;
        if (!material.enabled && material.id !== materialId) return null;
        if (onlyAllowedMaterisl && !(data?.__allowedMaterialIds ?? []).includes(material.id)) return null;

        return {
            id: material.id,
            name: getTranslatedAttribute(material, 'description'),
            // description: material.code + ' - ' + t('dialogs.material_details.is_plastic_' + (material.isPlastic ? 'yes' : 'no') + '_extended'),
            description: getTranslatedAttribute(material, 'code'),
        };
    }, [getTranslatedAttribute, materialId, onlyPlastic, onlyNotPlastic, data?.__allowedMaterialIds, onlyAllowedMaterisl]);

    const additionalEntries = useMemo(() => {
        if (!data?.__allowOtherMaterial) return [];

        return [
            {
                id: -1,
                name: t('dialogs.material_select.other')
            }
        ];
    }, [t, data?.__allowOtherMaterial]);

    const selectProps = {
        open: false,
        onOpen: () => setIsSelectModalOpen(true)
    };

    return (
        <>
            <CustomInput {...baseInputProps} name={name ?? 'materialId'} select SelectProps={selectProps} >
                {(materialData && materialData.id === parseInt(materialId, 10)) ? (
                    <MenuItem value={materialData.id} >
                        {getTranslatedAttribute(materialData, 'description')}
                    </MenuItem>
                ) : (
                    <MenuItem />
                )}
            </CustomInput>

            <ApiEntitySelectDialog
                open={isSelectModalOpen}
                onClose={onMaterialSelect}
                title={t('dialogs.material_select.title')}
                noEntityFoundMessage={t('dialogs.material_select.no_material_found')}
                url={'/materials'}
                map={map}
                additionalEntries={additionalEntries}
            />
        </>
    );
};

export default MaterialSelect;
